import React from 'react';

import { type AnyFunction, isArray } from 'utils/check';

export const useDebouncedClick = <ARGS = any>(
  onClick?: (args: ARGS) => void,
  debounceClickTime?: number,
) => {
  const hasClickedRef = React.useRef(true);

  return (args: ARGS) => {
    if (!hasClickedRef.current) return;
    hasClickedRef.current = false;

    const timoutId = setTimeout(() => {
      hasClickedRef.current = true;
      clearTimeout(timoutId);
    }, debounceClickTime || 500);

    onClick?.(args);
  };
};

export const useWindowHasChanges = (enabled?: boolean) => {
  const [hasScrollChanges, setHasScrollChanges] = React.useState(0);
  const [hasSizeChanges, setHasSizeChanges] = React.useState(0);
  const deferredScrollChanged = React.useDeferredValue(hasScrollChanges);
  const deferredSizeChanged = React.useDeferredValue(hasSizeChanges);

  React.useEffect(() => {
    if (enabled === false) return;

    const changeScrollHandler = () => {
      setHasScrollChanges((v) => v + 1);
    };

    const changeSizeHandler = () => {
      setHasSizeChanges((v) => v + 1);
    };

    document.addEventListener('scroll', changeScrollHandler);
    document.addEventListener('resize', changeSizeHandler);

    return () => {
      document.removeEventListener('scroll', changeScrollHandler);
      document.removeEventListener('resize', changeSizeHandler);
    };
  }, [enabled]);

  return {
    scrollChanges: deferredScrollChanged,
    sizeChanges: deferredSizeChanged,
  };
};

export const useClickOutside = (
  refs:
    | React.RefObject<HTMLDivElement | HTMLButtonElement | null>
    | React.RefObject<HTMLDivElement | HTMLButtonElement | null>[],
  onClickOutside: AnyFunction,
) => {
  React.useEffect(() => {
    const handleClick: EventListener = (event) => {
      const path = event.composedPath?.() || [];
      const targetRefs = isArray(refs) ? refs : [refs];

      const clickedInsideSomeRef = targetRefs.some((ref) => {
        const el = ref?.current;
        return el && (el.contains(event.target as Node) || path.includes(el));
      });

      if (!clickedInsideSomeRef) {
        onClickOutside(event);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClickOutside]);
};

export const useClipboardCopy = () => {
  const [copiedStatus, setCopiedStatus] = React.useState<'non' | 'success' | 'error'>(
    'non',
  );

  const doCopy = (copyValue: string) => {
    navigator.clipboard
      .writeText(copyValue)
      .then(() => {
        setCopiedStatus('success');
      })
      .catch(() => {
        setCopiedStatus('error');
      })
      .finally(() => {
        setTimeout(() => {
          setCopiedStatus('non');
        }, 3000);
      });
  };

  return { doCopy, copiedStatus };
};

export const useDebounce = (value: string | number, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};
